import { useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Dropdown,
    Field,
    MessageBar,
    MessageBarBody,
    Option,
    Spinner,
    Switch,
    Text,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { useUserPreferences } from "../../contexts/UserPreferencesContext";
import { isSameRole, UserPreferences, UserPreferencesUpdate } from "../../services/UserPreferencesService";
import { useToast } from "../ToastProvider";

const initialRole = (preferences: UserPreferences, roles: string[]): string => {
    if (preferences.needsRoleSelection || !preferences.role) {
        return "";
    }
    if (roles.length === 0) {
        return preferences.role;
    }
    return roles.find((role) => isSameRole(role, preferences.role)) ?? "";
};

interface SettingsFormProps {
    preferences: UserPreferences;
    roles: string[];
    isSaving: boolean;
    setIsSaving: (isSaving: boolean) => void;
    onClose: () => void;
}

// Fields the user has not edited follow the live preferences, so a refresh from another tab while the dialog
// is open neither enables Save nor writes the older value back.
function SettingsForm({ preferences, roles, isSaving, setIsSaving, onClose }: SettingsFormProps) {
    const { save } = useUserPreferences();
    const { showToast } = useToast();
    const [editedRole, setEditedRole] = useState<string | null>(null);
    const [editedEmailEnabled, setEditedEmailEnabled] = useState<boolean | null>(null);
    const [error, setError] = useState<string | null>(null);

    const selectedRole = editedRole ?? initialRole(preferences, roles);
    const emailEnabled = editedEmailEnabled ?? preferences.emailNotificationsEnabled;
    const roleChanged = editedRole !== null
        && selectedRole !== ""
        && (preferences.needsRoleSelection || !isSameRole(selectedRole, preferences.role));
    const emailChanged = editedEmailEnabled !== null && emailEnabled !== preferences.emailNotificationsEnabled;
    const canSave = (roleChanged || emailChanged) && selectedRole !== "" && !isSaving;

    const handleSave = async () => {
        if (!canSave) {
            return;
        }

        const update: UserPreferencesUpdate = {};
        if (roleChanged) {
            update.roleName = selectedRole;
        }
        if (emailChanged) {
            update.emailNotificationsEnabled = emailEnabled;
        }

        setIsSaving(true);
        setError(null);
        const result = await save(update);
        setIsSaving(false);
        if (!result.ok) {
            setError(result.error);
            return;
        }

        showToast({ title: "Settings saved", intent: "success" });
        onClose();
    };

    return (
        <DialogBody>
            <DialogTitle action={<CloseButton onClick={onClose} disabled={isSaving} />}>Settings</DialogTitle>
            <DialogContent className="flex flex-col gap-4">
                <Field label="Role" hint={roles.length === 0 ? "Roles could not be loaded right now." : undefined}>
                    <Dropdown
                        placeholder="Select your role"
                        value={selectedRole}
                        selectedOptions={selectedRole ? [selectedRole] : []}
                        onOptionSelect={(_, data) => setEditedRole(data.optionValue ?? "")}
                        disabled={isSaving || roles.length === 0}
                    >
                        {roles.map((role) => (
                            <Option key={role} value={role}>
                                {role}
                            </Option>
                        ))}
                    </Dropdown>
                </Field>
                <Field hint="Receive emails sent to your role (e.g. analysis and powder order updates).">
                    <Switch
                        label="Email notifications"
                        checked={emailEnabled}
                        onChange={(_, data) => setEditedEmailEnabled(data.checked)}
                        disabled={isSaving}
                    />
                </Field>
                {error && (
                    <MessageBar intent="error">
                        <MessageBarBody>{error}</MessageBarBody>
                    </MessageBar>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    appearance="primary"
                    disabled={!canSave}
                    icon={isSaving ? <Spinner size="tiny" /> : undefined}
                    onClick={handleSave}
                >
                    Save
                </Button>
                <Button appearance="secondary" disabled={isSaving} onClick={onClose}>
                    Cancel
                </Button>
            </DialogActions>
        </DialogBody>
    );
}

function CloseButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
    return <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={onClick} disabled={disabled} />;
}

/** The user's role and email notification settings, opened from the settings button. Render it inside the ToastProvider. */
export default function UserSettingsDialog() {
    const { preferences, roles, status, isSettingsOpen, closeSettings, reload } = useUserPreferences();
    const [isSaving, setIsSaving] = useState(false);
    // The dialog keeps its content mounted after closing, so the form is re-keyed on every open to drop cancelled edits.
    const [openCount, setOpenCount] = useState(0);
    const [wasOpen, setWasOpen] = useState(isSettingsOpen);
    if (isSettingsOpen !== wasOpen) {
        setWasOpen(isSettingsOpen);
        if (isSettingsOpen) {
            setOpenCount((count) => count + 1);
        }
    }

    const close = () => {
        if (!isSaving) {
            closeSettings();
        }
    };

    return (
        <Dialog
            open={isSettingsOpen}
            onOpenChange={(_, data) => {
                if (!data.open) {
                    close();
                }
            }}
        >
            <DialogSurface>
                {status === "ready" && preferences ? (
                    <SettingsForm
                        key={openCount}
                        preferences={preferences}
                        roles={roles}
                        isSaving={isSaving}
                        setIsSaving={setIsSaving}
                        onClose={closeSettings}
                    />
                ) : (
                    <DialogBody>
                        <DialogTitle action={<CloseButton onClick={close} />}>Settings</DialogTitle>
                        <DialogContent>
                            {status === "loading" ? (
                                <Spinner label="Loading your settings..." />
                            ) : (
                                <Text as="p">Your settings could not be loaded right now.</Text>
                            )}
                        </DialogContent>
                        <DialogActions>
                            {status === "unavailable" && (
                                <Button appearance="primary" onClick={() => void reload()}>
                                    Retry
                                </Button>
                            )}
                            <Button appearance="secondary" onClick={close}>
                                Cancel
                            </Button>
                        </DialogActions>
                    </DialogBody>
                )}
            </DialogSurface>
        </Dialog>
    );
}
